/**
 * Composable to handle image URL generation for R2 storage
 */
export function useImageUrl() {
  const config = useRuntimeConfig()
  const publicUrl = config.public.storagePublicUrl || "https://files.blaster.hereticnyc.com"
  
  /**
   * Generates an image URL based on the current environment
   * @param groupAlias - The group/organization alias
   * @param emailAlias - The email alias
   * @param filename - The image filename
   * @param preventCache - Whether to append a timestamp to prevent caching
   * @returns The complete URL to the image
   */
  function getImageUrl(groupAlias: string, emailAlias: string, filename: string, preventCache = false): string {
    // If the filename already starts with http/https, it's an absolute URL
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      let url = filename
      // Append timestamp to external URLs if preventCache is true
      if (preventCache) {
        const timestamp = Date.now()
        const separator = url.includes('?') ? '&' : '?'
        url = `${url}${separator}t=${timestamp}`
      }
      return url
    }
    
    // Construct the URL with the base URL and image path
    let url = `${publicUrl}/images/${groupAlias}/${emailAlias}/${filename}`.replace(/([^:])\/+/g, '$1/')
    
    // Append timestamp to prevent caching if needed
    if (preventCache) {
      const timestamp = Date.now()
      url = `${url}${url.includes('?') ? '&' : '?'}t=${timestamp}`
    }
    
    return url
  }
  
  /**
   * Generate a URL for an image from R2 storage using a full path
   * @param path - The image path (typically groupAlias/emailAlias/filename)
   * @param useApi - Whether to use the API endpoint (for signed URLs) instead of CDN
   * @param preventCache - Whether to append a timestamp to prevent caching
   * @returns The full URL to the image
   */
  function getImageUrlByPath(path: string, useApi = false, preventCache = false): string {
    if (!path) return ''
    
    // If path is already a URL, return it
    if (path.startsWith('http://') || path.startsWith('https://')) {
      let url = path
      // Append timestamp to external URLs if preventCache is true
      if (preventCache) {
        const timestamp = Date.now()
        const separator = url.includes('?') ? '&' : '?'
        url = `${url}${separator}t=${timestamp}`
      }
      return url
    }
    
    // Clean up path to avoid double slashes
    const cleanPath = path.replace(/^\/+/, '').replace(/\/+/g, '/')
    
    // If useApi is true, use the API endpoint that will redirect to a signed URL
    if (useApi) {
      let url = `/api/v1/storage/${cleanPath}`
      if (preventCache) {
        const timestamp = Date.now()
        url = `${url}${url.includes('?') ? '&' : '?'}t=${timestamp}`
      }
      return url
    }
    
    // Otherwise, use the public CDN URL
    let url = `${publicUrl}/images/${cleanPath}`.replace(/([^:])\/+/g, '$1/')
    
    // Append timestamp to prevent caching if needed
    if (preventCache) {
      const timestamp = Date.now()
      url = `${url}${url.includes('?') ? '&' : '?'}t=${timestamp}`
    }
    
    return url
  }

  return {
    getImageUrl,
    getImageUrlByPath
  }
}